// Tarjeta de un dato de contacto (teléfono, correo) con su ícono y botón «Copiar», para los cajones de detalle.
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

type IconType = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

/** Botón «Copiar» de las tarjetas de contacto: dice «Copiado» un momento. */
function CopyButton({ value, label }: { value: string; label: string }) {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => {
            setCopied(false);
        }, 1400);
        return () => {
            clearTimeout(timer);
        };
    }, [copied]);
    return (
        <button
            type="button"
            title={`Copiar ${label}`}
            aria-label={copied ? `${label} copiado` : `Copiar ${label}`}
            aria-live="polite"
            onClick={() => {
                // Sin portapapeles (http o permiso negado) igual confirma: el valor está a la vista.
                navigator.clipboard.writeText(value).catch(() => undefined);
                setCopied(true);
            }}
            className={cn(
                'inline-flex h-[22px] flex-none items-center gap-0.5 rounded border border-border bg-card px-1 text-[10px] font-medium hover:bg-accent',
                copied && 'border-success text-success',
            )}
        >
            {copied ? (
                <Check className="size-3" aria-hidden />
            ) : (
                <Copy className="size-3" aria-hidden />
            )}
            {copied ? 'Copiado' : 'Copiar'}
        </button>
    );
}

export interface ContactCardProps {
    icon: IconType;
    label: string;
    /** Lo que se ve; `null` pinta `missing` en su lugar. */
    value: string | null;
    /** Lo que se copia, sin formato. */
    copyValue: string | null;
    /** Por qué falta el dato, cuando `value` es `null`. */
    missing: ReactNode;
    className?: string;
}

export function ContactCard({
    icon: Icon,
    label,
    value,
    copyValue,
    missing,
    className,
}: ContactCardProps) {
    return (
        <div
            className={cn(
                'flex min-w-0 items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5',
                className,
            )}
        >
            <span className="flex size-6 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Icon className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    {label}
                </div>
                <div className="truncate text-[11.5px] font-semibold tabular-nums">
                    {value ?? missing}
                </div>
            </div>
            {value !== null && copyValue ? <CopyButton value={copyValue} label={label} /> : null}
        </div>
    );
}
